/**
 * Demo Limit Service (Slice B + F)
 * ================================
 * Thin client for the DEMO daily view-quota endpoints:
 *   GET  /api/v1/products/demo/usage-summary   (Slice A) — meter snapshot
 *   POST /api/v1/products/demo/reminder-optin  (Slice F) — "email me tomorrow"
 * The summary fetch mirrors the authoritative snapshot into demoLimitStore so
 * the usage meter is accurate even before the first cell-detail load of the day
 * (the per-request X-Demo-Views-* headers keep it live thereafter).
 */
import { authenticatedFetch } from './authService.js'
import { getCellNormalized, invalidateExplorerCache } from '../modules/dataService.js'
import {
  applyUsageSummary,
  applyUnlockResult,
  isDemoCatalogUnlocked,
  isCellUnlockedToday,
  notifyDetailUnlock,
  openExportUpsell,
  openLimitModalOrToast,
  markCellViewedToday,
} from '../state/demoLimitStore.js'

const API_BASE = import.meta.env?.VITE_API_URL || window.BATEMO_API?.baseUrl || ''

let _inFlight = null

/**
 * Fetch the demo usage summary and apply it to the store. No-op for non-demo
 * users. De-duplicated: concurrent callers share one request. Never throws —
 * the meter degrades gracefully to the localStorage mirror / header updates.
 *
 * @param {boolean} [force] - bypass the in-flight dedupe
 * @returns {Promise<object|null>} the summary JSON, or null on skip/failure
 */
export async function refreshDemoUsageSummary(force = false) {
  if (!isDemoCatalogUnlocked()) return null
  if (_inFlight && !force) return _inFlight

  const base = String(API_BASE).replace(/\/?$/, '')
  const url = `${base}/products/demo/usage-summary`

  _inFlight = authenticatedFetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })
    .then(async (res) => {
      if (!res.ok) {
        console.warn('[demoLimitService] usage-summary fetch failed:', res.status)
        return null
      }
      const json = await res.json()
      applyUsageSummary(json)
      return json
    })
    .catch((err) => {
      console.warn('[demoLimitService] usage-summary error:', err?.message || err)
      return null
    })
    .finally(() => {
      _inFlight = null
    })

  return _inFlight
}

/**
 * Shared eager-load-or-block gate for USER-INITIATED "add to compare" actions —
 * the "+" icon in the grid/table and the compare-panel Quick-Add. Adding a cell
 * to Compare makes a compare view deep-dive it (a view the backend counts), so
 * we load the detail eagerly NOW, at add time, instead of lazily on the later
 * compare-view load. That keeps the always-on meter and the "explored today"
 * list in sync across EVERY user add path, and blocks an over-the-limit add
 * before a phantom chip can appear.
 *
 * Only the ADD path calls this — removing from compare never counts. Programmatic
 * adds (benchmarking, tours, command bar) must NOT go through here; they call
 * filterStore.addToCompare directly and stay uncounted.
 *
 * Behaviour mirrors the original ComparePanel.handleAddCell logic (now DRY):
 *   - Non-demo (PRO/admin/free): no quota → true immediately, no deep-dive load.
 *   - Demo + load OK: record the cell viewed-today (idempotent) → true.
 *   - Demo + DEMO_LIMIT (429): the central getCellNormalized interceptor already
 *     opened the conversion overlay → false (caller must NOT add — no ghost chip).
 *   - Demo + any other load error: true (let the compare view surface its own
 *     error/retry), but do NOT record it viewed (the deep-dive wasn't confirmed).
 *
 * @param {object} cell - { id | product_id, name?, manufacturer? }
 * @returns {Promise<boolean>} true → caller may add; false → abort the add.
 */
export async function consumeCompareDeepDive(cell) {
  const cellId = cell && (cell.id || cell.product_id)
  if (!cellId) return false
  // Non-demo tiers carry no daily quota — add straight away, no eager load.
  if (!isDemoCatalogUnlocked()) return true

  // Capture unlock state BEFORE the load so we can detect a genuine new-unlock
  // transition afterwards (markCellViewedToday adds the cell to viewedToday).
  const wasUnlocked = isCellUnlockedToday(cellId)

  try {
    // Same field-gating variant the compare views use → the eager load and the
    // later view load share ONE cache entry (no second backend hit / no double
    // count); the backend is idempotent per cell/day regardless.
    await getCellNormalized(cellId, { fieldGating: 'demo' })
  } catch (err) {
    if (err?.code === 'DEMO_LIMIT' || err?.detail?.code === 'demo_daily_view_limit') {
      // At-limit add of a not-yet-seen cell — overlay already surfaced centrally.
      return false
    }
    // Non-quota failure: still allow the add; do NOT claim it viewed.
    return true
  }

  // Load succeeded → deep-dive consumed (or already counted today). Record it in
  // the overlay list; idempotent by product_id so the backend won't double-count.
  markCellViewedToday({
    product_id: cellId,
    name: cell.name,
    manufacturer: cell.manufacturer,
  })

  // Refresh the grid so a just-unlocked cell loses its server-blurred thumbnail
  // AND its lock overlay. Both are keyed off non-reactive cell-object fields
  // (`cell.is_locked` / `cell.thumbnail_url`) that only a fresh explorer load
  // replaces with the sharp, is_locked=false version. The eager detail GET above
  // already consumed the Deep-Dive token server-side, so the cell is genuinely
  // unlocked — but unlike the in-place `unlockCell` path, the "+" add path never
  // invalidated the cache or bumped `demoUnlockVersion`, so MainAppPage's
  // reactive reload never fired and the frosted preview lingered. Mirror that
  // path now: drop the explorer cache, then bump the version via the canonical
  // `notifyDetailUnlock` bridge (which no-ops unless this was a genuine new
  // unlock, so a re-add of an already-opened cell triggers no needless reload).
  invalidateExplorerCache()
  notifyDetailUnlock(cellId, wasUnlocked)
  return true
}

/**
 * Globally unlock a cell for a demo user via a Deep-Dive token — the Grid/
 * Table/Plot surfaces call this (instead of `getCellNormalized`) when the
 * user clicks a locked cell/point. Success makes the cell un-gated
 * EVERYWHERE (Grid, Table, Plot, Compare) via the shared `viewedToday` /
 * `unlockedTodayIds` store, and invalidates the cached Explorer list so the
 * next Grid/Table/Plot load reflects the un-gated cell instead of serving a
 * stale cached snapshot from earlier today.
 *
 * - Non-demo (PRO/admin/free) → no-op, returns null, no request.
 * - Demo + success (200)      → applies the result to the store, drops the
 *   Explorer cache, returns the `{ cell, usage }` payload.
 * - Demo + at-limit (429, `demo_daily_view_limit`) → opens the conversion
 *   overlay via `openLimitModalOrToast`, returns null. Never throws for this
 *   case — callers must not treat a block as an error.
 * - Any other failure → rethrown, so the caller can surface its own error UI.
 *
 * @param {object} params
 * @param {string} [params.handle] - the cell's public handle/slug (if known)
 * @param {string} params.productId - the cell's product id
 * @param {string} [params.surface] - 'plot' | 'grid' | 'table' | 'compare'
 * @returns {Promise<object|null>} the `{ cell, usage }` payload, or null
 */
export async function unlockCell({ handle, productId, surface = 'plot' } = {}) {
  if (!isDemoCatalogUnlocked()) return null

  const base = String(API_BASE).replace(/\/?$/, '')
  const url = `${base}/products/demo/unlock`

  try {
    const res = await authenticatedFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ handle, product_id: productId, surface }),
    })

    if (!res.ok) {
      let errorData = null
      try {
        errorData = await res.json()
      } catch (e) {
        /* non-JSON error body — fall through to the generic Error below */
      }
      const detail = errorData?.detail
      if (res.status === 429 && detail && typeof detail === 'object' && detail.code === 'demo_daily_view_limit') {
        const limitErr = new Error(detail.message || 'Daily cell-view limit reached')
        limitErr.code = 'DEMO_LIMIT'
        limitErr.status = 429
        limitErr.detail = detail
        limitErr.demoLimit = {
          viewed_count: detail.viewed_count,
          daily_limit: detail.daily_limit,
          reset_at: detail.reset_at,
        }
        throw limitErr
      }
      throw new Error(`unlockCell failed: HTTP ${res.status}`)
    }

    const json = await res.json()
    applyUnlockResult(json)
    invalidateExplorerCache()
    return json
  } catch (err) {
    if (err?.code === 'DEMO_LIMIT' || err?.detail?.code === 'demo_daily_view_limit') {
      openLimitModalOrToast(err.demoLimit || {})
      return null
    }
    throw err
  }
}

/**
 * Gate an export take-away for demo users (surface-limits slice).
 *
 * Demo users explore the full catalogue freely; the export is the single
 * PRO-only lever. This helper decides whether the caller may run the real
 * export:
 *   - Non-demo → returns false; the caller proceeds with the real export.
 *   - Demo     → fire-and-forget records the blocked-export signal, opens the
 *                upsell modal, and returns true; the caller must NOT export.
 *
 * The recording POST is never awaited and never throws — the upsell opens
 * regardless of the backend's response.
 *
 * @param {string} surface - 'free_plot' | 'radar' | 'cell_selection'
 * @returns {boolean} true if the export was gated (caller must abort), else false
 */
export function blockDemoExport(surface) {
  if (!isDemoCatalogUnlocked()) return false

  // Fire-and-forget: record the conversion signal, swallow all errors.
  try {
    const base = String(API_BASE).replace(/\/?$/, '')
    const url = `${base}/products/demo/export-blocked`
    authenticatedFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ surface }),
    }).catch(() => {
      /* fail-quiet — the upsell opens regardless */
    })
  } catch (err) {
    /* never block the UI on telemetry */
  }

  openExportUpsell(surface)
  return true
}

/**
 * Record that a demo user hit a locked/teaser premium metric (metric-tiering
 * slice). Fire-and-forget conversion signal: POST /products/demo/metric-locked
 * with the metric category. No-op for non-demo users. Never awaited, never
 * throws — the upsell UI opens regardless of the backend's response.
 *
 * @param {string} category - 'electrical' | 'thermal' | 'quality' | …
 */
export function recordMetricLocked(category) {
  if (!isDemoCatalogUnlocked()) return

  try {
    const base = String(API_BASE).replace(/\/?$/, '')
    const url = `${base}/products/demo/metric-locked`
    authenticatedFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ category }),
    }).catch(() => {
      /* fail-quiet — the upsell opens regardless */
    })
  } catch (err) {
    /* never block the UI on telemetry */
  }
}

/**
 * Opt the current demo user into a "your deep-dives reset tomorrow" reminder
 * email (Slice F). Takes no body; the backend derives the recipient + send time
 * and de-dupes (NurtureEmailLog UNIQUE), so a repeat click is harmless.
 *
 * Never throws — returns the parsed result on success, or null on any failure
 * so the caller can fail quietly and not block the user.
 *
 * @returns {Promise<{scheduled:boolean, send_at?:string, reason?:string}|null>}
 */
export async function requestDemoReminder() {
  const base = String(API_BASE).replace(/\/?$/, '')
  const url = `${base}/products/demo/reminder-optin`
  try {
    const res = await authenticatedFetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) {
      console.warn('[demoLimitService] reminder-optin failed:', res.status)
      return null
    }
    return await res.json()
  } catch (err) {
    console.warn('[demoLimitService] reminder-optin error:', err?.message || err)
    return null
  }
}
